/**
 * hero-animated-chart.js
 * Hero animado de la home: Chart.js con animación manual por fases.
 * Fase 1 (0-33%): dibuja escenarios Base/Bull/Bear + fills.
 * Fase 2 (33-75%): dibuja consenso + "gotea" los precios reales.
 * Fase 3 (75-100%): valida la tesis (línea sólida, fade de las otras) + badge.
 */
(function() {
  'use strict';

  var FALLBACK = {
    ticker: 'ITX', name: 'Inditex', currency: '€',
    actual: 52.50, reportDate: '2026-01-15',
    base: { m6: 56.80, m12: 61.20, m24: 68.50 },
    bull: { m6: 59.40, m12: 67.30, m24: 78.90 },
    bear: { m6: 48.20, m12: 45.60, m24: 42.10 },
    validatedTesis: 'Base',
    evolution: [],
    analystTargets: []
  };

  /* ---------- Utils ---------- */

  function toNum(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n; }

  function parseISO(s) {
    if (!s) return null;
    var parts = String(s).split('-');
    if (parts.length < 3) return null;
    return new Date(+parts[0], (+parts[1]) - 1, +parts[2]);
  }

  function addMonths(d, m) {
    var n = new Date(d.getTime());
    n.setMonth(n.getMonth() + m);
    return n;
  }

  function parseJSONSafe(s, fallback) {
    if (!s) return fallback;
    try { return JSON.parse(s); } catch (e) { return fallback; }
  }

  function parseDataAttributes(container) {
    var ds = container.dataset;
    var actual = toNum(ds.actual);
    var reportDate = parseISO(ds.reportDate) || new Date();
    return {
      duration: parseInt(ds.duration, 10) || 12000,
      currency: ds.currency || '€',
      ticker: ds.ticker || '',
      company: ds.company || '',
      actual: actual || FALLBACK.actual,
      reportDate: reportDate,
      validatedTesis: (ds.validatedTesis || 'Base').trim(),
      base: { m6: toNum(ds['6m']) || FALLBACK.base.m6, m12: toNum(ds['12m']) || FALLBACK.base.m12, m24: toNum(ds['24m']) || FALLBACK.base.m24 },
      bull: { m6: toNum(ds.bull6m) || FALLBACK.bull.m6, m12: toNum(ds.bull12m) || FALLBACK.bull.m12, m24: toNum(ds.bull24m) || FALLBACK.bull.m24 },
      bear: { m6: toNum(ds.bear6m) || FALLBACK.bear.m6, m12: toNum(ds.bear12m) || FALLBACK.bear.m12, m24: toNum(ds.bear24m) || FALLBACK.bear.m24 },
      evolution: parseJSONSafe(ds.priceEvolution, []),
      analystTargets: parseJSONSafe(ds.analystTargets, []),
      alerts: [ds.alert1 || '', ds.alert2 || '', ds.alert3 || '']
    };
  }

  /**
   * Returns the subset of fullData that should be visible given progress 0→1
   * across the full line. Adds an interpolated point for partial segments.
   */
  function interpolatePoints(fullData, progress) {
    if (progress <= 0) return [fullData[0]];
    if (progress >= 1) return fullData.slice();
    var totalSegments = fullData.length - 1;
    var currentSegment = progress * totalSegments;
    var segIndex = Math.floor(currentSegment);
    var segProgress = currentSegment - segIndex;
    var result = fullData.slice(0, segIndex + 1);
    if (segIndex < totalSegments) {
      var from = fullData[segIndex];
      var to = fullData[segIndex + 1];
      result.push({
        x: from.x + (to.x - from.x) * segProgress,
        y: from.y + (to.y - from.y) * segProgress
      });
    }
    return result;
  }

  /* ---------- Icons for alerts ---------- */

  var ICONS = {
    info: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v1a3 3 0 0 0-3 3v1a3 3 0 0 0 1 2.22V15a3 3 0 0 0 3 3v1a3 3 0 0 0 6 0v-1a3 3 0 0 0 3-3v-2.78A3 3 0 0 0 20 10V9a3 3 0 0 0-3-3V5a3 3 0 0 0-5-3z"/></svg>',
    tracking: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/></svg>',
    success: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/></svg>'
  };

  /* ---------- Chart creation ---------- */

  function createChart(canvas, opts, tMin, tMax, yMin, yMax, swapBaseBear) {
    var ctx = canvas.getContext('2d');

    var baseColor = swapBaseBear ? '#dc2626' : '#3b82f6';
    var bearColor = swapBaseBear ? '#3b82f6' : '#dc2626';
    var fillBearBg = swapBaseBear ? 'rgba(59,130,246,0.06)' : 'rgba(220,38,38,0.06)';

    /* Dataset indices:
       0: Fill Bull→Base (visual only, no line)
       1: Fill Base→Bear
       2: Bull (optimista)
       3: Base
       4: Bear (pesimista)
       5: Consenso
       6: Precio Real
    */
    var datasets = [
      { label: '_fillBullBase', data: [{x:tMin,y:opts.actual}], borderColor: 'transparent', pointRadius: 0, fill: '+1', backgroundColor: 'rgba(22,163,74,0.08)', tension: 0 },
      { label: '_fillBaseBear', data: [{x:tMin,y:opts.actual}], borderColor: 'transparent', pointRadius: 0, fill: '+1', backgroundColor: fillBearBg, tension: 0 },
      { label: 'Optimista', data: [{x:tMin,y:opts.actual}], borderColor: '#16a34a', borderWidth: 1.6, borderDash: [4,3], pointRadius: 0, fill: false, tension: 0 },
      { label: 'Base', data: [{x:tMin,y:opts.actual}], borderColor: baseColor, borderWidth: 1.6, borderDash: [4,3], pointRadius: 0, fill: false, tension: 0 },
      { label: 'Pesimista', data: [{x:tMin,y:opts.actual}], borderColor: bearColor, borderWidth: 1.6, borderDash: [4,3], pointRadius: 0, fill: false, tension: 0 },
      { label: 'Consenso', data: [{x:tMin,y:opts.actual}], borderColor: '#e67e22', borderWidth: 1.5, borderDash: [6,4], pointRadius: 0, fill: false, tension: 0 },
      {
        label: 'Precio real',
        data: [],
        borderColor: '#5a4a3f',
        borderWidth: 2,
        pointRadius: 2.5,
        pointBackgroundColor: '#5a4a3f',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        fill: false,
        tension: 0.2
      }
    ];

    return new Chart(ctx, {
      type: 'line',
      data: { datasets: datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        },
        scales: {
          x: {
            type: 'linear',
            min: tMin,
            max: tMax,
            grid: { display: false },
            ticks: {
              color: 'rgba(0,0,0,0.45)',
              font: { size: 10 },
              callback: function(v) {
                var d = new Date(v);
                var diffMs = v - tMin;
                var months = Math.round(diffMs / (1000 * 60 * 60 * 24 * 30.44));
                if (months === 0) return 'Hoy';
                if (months >= 1) return '+' + months + 'm';
                return '';
              },
              maxTicksLimit: 6
            }
          },
          y: {
            min: yMin,
            max: yMax,
            grid: { color: 'rgba(0,0,0,0.04)' },
            ticks: {
              color: 'rgba(0,0,0,0.45)',
              font: { size: 10 },
              callback: function(v) { return opts.currency + v.toFixed(0); },
              maxTicksLimit: 5
            }
          }
        }
      }
    });
  }

  /* ---------- Alerts creation ---------- */

  function createAlerts(container, opts) {
    var alerts = [
      { type: 'info', phaseClass: 'phase1', text: opts.alerts[0], icon: ICONS.info },
      { type: 'tracking', phaseClass: 'phase2', text: opts.alerts[1], icon: ICONS.tracking },
      { type: 'success', phaseClass: 'phase3', text: opts.alerts[2], icon: ICONS.success }
    ];
    var els = [];
    alerts.forEach(function(a) {
      if (!a.text) { els.push(null); return; }
      var el = document.createElement('div');
      el.className = 'hac__alert hac__alert--' + a.type + ' hac__alert--' + a.phaseClass;
      el.innerHTML = a.icon + '<span>' + a.text + '</span>';
      container.appendChild(el);
      els.push(el);
    });
    return els;
  }

  /* ---------- Validation badge ---------- */

  function createValidationBadge(container, opts) {
    /* The hero narrative always validates the Optimista scenario */
    var potential = ((opts.bull.m12 - opts.actual) / opts.actual) * 100;
    var sign = potential >= 0 ? '+' : '';

    var el = document.createElement('div');
    el.className = 'hac__validation-badge hac__validation-badge--optimista';
    el.innerHTML =
      'Tesis Optimista confirmada ✓' +
      '<span class="hac__validation-sub">Potencial: ' + sign + potential.toFixed(1) + '% a 12 meses</span>';
    container.appendChild(el);
    return el;
  }

  function createDetectionMarker(container) {
    var el = document.createElement('div');
    el.className = 'hac__detection';
    el.innerHTML =
      '<span class="hac__detection-dot"></span>' +
      '<span class="hac__detection-label">Oportunidad detectada</span>';
    container.appendChild(el);
    return el;
  }

  function createValidationTags(container) {
    /* Tags removed by request — return a detached node so existing
       classList / querySelectorAll calls remain inert. */
    var wrap = document.createElement('div');
    wrap.className = 'hac__validation-tags';
    return wrap;
  }

  /* ---------- Main init ---------- */

  function initOne(container) {
    if (container.dataset.animated === '1') return;
    if (typeof Chart === 'undefined') {
      setTimeout(function() { initOne(container); }, 150);
      return;
    }

    var opts = parseDataAttributes(container);
    var canvas = container.querySelector('canvas.hac__canvas');
    if (!canvas) return;

    /* Timeline */
    var t0 = opts.reportDate.getTime();
    var t6 = addMonths(opts.reportDate, 6).getTime();
    var t12 = addMonths(opts.reportDate, 12).getTime();
    var t24 = addMonths(opts.reportDate, 24).getTime();

    /* Full scenario data.
       Base is softened (60% of its real growth) so the hero shows a more
       moderate "most likely" trajectory and the Optimista line clearly
       separates from it. Bull/Bear stay on their real metafield values. */
    var BASE_SOFTEN = 0.6;
    function softenBase(v) {
      return opts.actual + (v - opts.actual) * BASE_SOFTEN;
    }
    var fullBase = [
      {x:t0,  y:opts.actual},
      {x:t6,  y:softenBase(opts.base.m6)},
      {x:t12, y:softenBase(opts.base.m12)},
      {x:t24, y:softenBase(opts.base.m24)}
    ];
    var fullBull = [{x:t0,y:opts.actual}, {x:t6,y:opts.bull.m6}, {x:t12,y:opts.bull.m12}, {x:t24,y:opts.bull.m24}];
    var fullBear = [{x:t0,y:opts.actual}, {x:t6,y:opts.bear.m6}, {x:t12,y:opts.bear.m12}, {x:t24,y:opts.bear.m24}];

    /* Y range */
    var allY = [opts.actual,
      opts.base.m6, opts.base.m12, opts.base.m24,
      opts.bull.m6, opts.bull.m12, opts.bull.m24,
      opts.bear.m6, opts.bear.m12, opts.bear.m24];
    var yMin = Math.min.apply(null, allY) * 0.92;
    var yMax = Math.max.apply(null, allY) * 1.08;

    /**
     * Synthesize a real-price trajectory that starts at `actual` and ends
     * right on the Optimista line near t6 (the narrative of the hero is
     * that the market confirms the Optimista scenario). Real metafield data
     * is intentionally NOT used here: the animation is a branded narrative.
     */
    var evoCount = 18;
    var evoPoints = [];
    for (var j = 0; j < evoCount; j++) {
      var ratio = (j + 1) / evoCount;
      /* ease-in-out so movement feels organic */
      var eased = ratio < 0.5
        ? 2 * ratio * ratio
        : 1 - Math.pow(-2 * ratio + 2, 2) / 2;
      /* Base trajectory: actual → bull.m6 */
      var target = opts.actual + (opts.bull.m6 - opts.actual) * eased;
      /* Small jitter ±1.2% to look realistic */
      var jitter = Math.sin(j * 1.35) * 0.012 + Math.cos(j * 0.7) * 0.006;
      evoPoints.push({ x: t0 + (t6 - t0) * ratio, y: target * (1 + jitter) });
    }

    /**
     * Consensus line: spans 70% of the scenarios length (from t0 to
     * t0 + 0.7 * (t24 - t0)), starts straight near `actual` and drifts
     * upward very gently. Ends at a modest target between actual and
     * softened base.m12 so it stays clearly below Optimista.
     */
    var tConsensusEnd = t0 + (t24 - t0) * 0.7;
    var consensusPointsCount = 9;
    var consensusStart = opts.actual * 0.99;
    var softBaseM12 = softenBase(opts.base.m12);
    /* When colors are swapped, the blue line is Bear (goes down).
       Consensus should track slightly above the bear trajectory. */
    var consensusEnd;
    if (swapBaseBear) {
      /* Midpoint between softened base and bear, biased toward base (60/40) */
      var bearM12 = opts.bear.m12;
      consensusEnd = softenBase(opts.base.m12) * 0.4 + bearM12 * 0.6;
    } else {
      consensusEnd = opts.actual + (softBaseM12 - opts.actual) * 0.75;
    }
    var consensusLine = [];
    for (var k = 0; k < consensusPointsCount; k++) {
      var cRatio = k / (consensusPointsCount - 1);
      var cTarget = consensusStart + (consensusEnd - consensusStart) * cRatio;
      var cWave = 0;
      if (cRatio > 0.3) {
        cWave = Math.sin((k - 2) * 1.6) * 0.009;
      }
      consensusLine.push({ x: t0 + (tConsensusEnd - t0) * cRatio, y: cTarget * (1 + cWave) });
    }

    /* Create chart */
    var swapBaseBear = container.hasAttribute('data-swap-base-bear');
    var chart = createChart(canvas, opts, t0, t24, yMin, yMax, swapBaseBear);

    /* Alerts + validation badge + tags + detection marker */
    var alertEls = createAlerts(container, opts);
    var validationBadge = createValidationBadge(container, opts);
    var validationTags = createValidationTags(container);
    var detectionMarker = createDetectionMarker(container);

    /* Steps */
    var root = container.closest('section.hac') || container.closest('[data-hac-host]');
    var stepEls = root ? Array.from(root.querySelectorAll('.hac__step')) : [];

    /* Detection point — needed by both static and animated modes */
    var DRAW_AT_TRIGGER = 0.44;
    var detectionIdx = Math.max(0, Math.floor(evoPoints.length * DRAW_AT_TRIGGER) - 1);
    var detectionPoint = evoPoints[detectionIdx] || evoPoints[0];

    /* ---------- Static / teaser modes: render final state immediately ---------- */
    var chartMode = (container.dataset.mode || 'animated').trim();
    if (chartMode === 'static' || chartMode === 'teaser') {
      /* 1. Draw all scenario lines */
      completePhase1();
      /* 2. Draw all real-price + consensus points */
      completeDrawing();

      /* 3. Dialog-only color adjustments:
            - Swap Base (blue→red) and Pesimista (red→blue)
            - Move Consenso trajectory near the Pesimista (now blue) line */
      chart.data.datasets[3].borderColor = '#dc2626';  /* Base → rojo */
      chart.data.datasets[4].borderColor = '#3b82f6';  /* Pesimista → azul */
      chart.data.datasets[1].backgroundColor = 'rgba(59,130,246,0.06)'; /* fill base→bear → tinte azul */

      /* Rebuild consenso near pesimista (bear) trajectory */
      var staticConsensus = [];
      var cLen = consensusLine.length;
      for (var ci = 0; ci < cLen; ci++) {
        var cRatio = ci / (cLen - 1);
        /* Interpolate along bear trajectory, slightly above */
        var bearTarget = opts.actual + (opts.bear.m6 - opts.actual) * cRatio * 1.1;
        /* Small wave for realism */
        var cWave = ci > 1 ? Math.sin(ci * 1.4) * (opts.actual * 0.008) : 0;
        staticConsensus.push({ x: consensusLine[ci].x, y: bearTarget + cWave });
      }
      chart.data.datasets[5].data = staticConsensus;

      /* Override detection point: place it ~70% through the evo trajectory
         so it sits ON the real-price line, between the blue (pesimista) and
         green (optimista) lines, visually closer to the green. */
      var staticDetIdx = Math.floor(evoPoints.length * 0.70);
      detectionPoint = evoPoints[staticDetIdx] || evoPoints[evoPoints.length - 1];

      chart.update('none');
      detectionMarker.classList.add('is-visible');
      /* 5. Show validation badge + tags instantly (no transition delay) */
      validationBadge.classList.add('is-visible');
      validationTags.classList.add('is-visible');
      validationTags.querySelectorAll('.hac__validation-tag').forEach(function(t) { t.style.transitionDelay = '0s'; });
      /* 6. Show only alert 3 (final) */
      if (alertEls[2]) alertEls[2].classList.add('is-visible');
      /* 7. Steps: all done */
      stepEls.forEach(function(el) { el.classList.add('is-done'); });

      /* 8. Defer detection marker positioning until container is visible
            (popup starts display:none → canvas has 0 dimensions at init) */
      (function deferPosition() {
        var rect = container.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          chart.resize();
          chart.update('none');
          positionDetectionMarker();
        } else {
          setTimeout(deferPosition, 150);
        }
      })();

      /* 9. Teaser overlay text */
      if (chartMode === 'teaser') {
        var overlayText = container.dataset.teaserOverlayText || 'Revela esta tesis con tu email';
        var veil = document.createElement('div');
        veil.className = 'hac__teaser-veil';
        veil.textContent = overlayText;
        container.appendChild(veil);
      }
      container.dataset.animated = '1';
      return; /* Skip animation loop entirely */
    }

    /* Animation timings (fraction of total duration)
       PHASE1_END: scenarios finished drawing
       TRIGGER:    real price meets the Optimista line → validation starts
                   (from here, validation visuals AND drawing run in parallel
                   until 1.0)
       DRAW_END:   progress at which real+consensus drawing reaches 100%
                   (slightly before 1.0 so validation effects have a final
                   moment of emphasis on their own)
    */
    var PHASE1_END = 0.26;
    var TRIGGER = 0.42;
    var DRAW_END = 0.92;
    /* Alert windows (fractions of total).
       The "tesis confirmada" alert (index 2) starts exactly at TRIGGER
       so it fires in sync with the validation badge, the tags and the
       step-3 state change on the right. */
    var ALERT_WINDOWS = [
      { start: 0.05, end: 0.26 },
      { start: 0.28, end: 0.41 },
      { start: 0.42, end: 1.0 }
    ];

    var rafId = null;
    var startTime = null;

    function resetState() {
      /* Reset datasets to a single initial point */
      chart.data.datasets[0].data = [{x:t0,y:opts.actual}];
      chart.data.datasets[1].data = [{x:t0,y:opts.actual}];
      chart.data.datasets[2].data = [{x:t0,y:opts.actual}];
      chart.data.datasets[3].data = [{x:t0,y:opts.actual}];
      chart.data.datasets[4].data = [{x:t0,y:opts.actual}];
      chart.data.datasets[5].data = [{x:t0,y:opts.actual}];
      chart.data.datasets[6].data = [];
      /* Reset styles */
      chart.data.datasets[2].borderWidth = 1.6;
      chart.data.datasets[3].borderWidth = 1.6;
      chart.data.datasets[4].borderWidth = 1.6;
      chart.data.datasets[2].borderDash = [4,3];
      chart.data.datasets[3].borderDash = [4,3];
      chart.data.datasets[4].borderDash = [4,3];
      chart.data.datasets[2].borderColor = '#16a34a';
      chart.data.datasets[3].borderColor = swapBaseBear ? '#dc2626' : '#3b82f6';
      chart.data.datasets[4].borderColor = swapBaseBear ? '#3b82f6' : '#dc2626';
      chart.data.datasets[0].backgroundColor = 'rgba(22,163,74,0.0)';
      chart.data.datasets[1].backgroundColor = 'rgba(0,0,0,0.0)';
      chart.update('none');

      alertEls.forEach(function(el) { if (el) el.classList.remove('is-visible'); });
      validationBadge.classList.remove('is-visible');
      validationTags.classList.remove('is-visible');
      detectionMarker.classList.remove('is-visible');
      detectionMarker.style.left = '';
      detectionMarker.style.top = '';
      stepEls.forEach(function(s) { s.classList.remove('is-active'); s.classList.remove('is-done'); });
    }

    function updatePhase1(p) {
      /* p: 0→1 within phase 1 */
      /* The three scenario lines draw simultaneously (0-0.85),
         then fills fade in (0.85-1.0). */
      var lineP = Math.min(p / 0.85, 1);
      chart.data.datasets[2].data = interpolatePoints(fullBull, lineP);
      chart.data.datasets[3].data = interpolatePoints(fullBase, lineP);
      chart.data.datasets[4].data = interpolatePoints(fullBear, lineP);

      var fillP = Math.max(Math.min((p - 0.85) / 0.15, 1), 0);
      var fillBearRgb = swapBaseBear ? '59,130,246' : '220,38,38';
      if (lineP >= 1) {
        chart.data.datasets[0].data = fullBull.slice();
        chart.data.datasets[0].backgroundColor = 'rgba(22,163,74,' + (0.08 * fillP).toFixed(3) + ')';
        chart.data.datasets[1].data = fullBase.slice();
        chart.data.datasets[1].backgroundColor = 'rgba(' + fillBearRgb + ',' + (0.06 * fillP).toFixed(3) + ')';
      }
    }

    function completePhase1() {
      chart.data.datasets[2].data = fullBull.slice();
      chart.data.datasets[3].data = fullBase.slice();
      chart.data.datasets[4].data = fullBear.slice();
      chart.data.datasets[0].data = fullBull.slice();
      chart.data.datasets[1].data = fullBase.slice();
      chart.data.datasets[0].backgroundColor = 'rgba(22,163,74,0.08)';
      chart.data.datasets[1].backgroundColor = swapBaseBear ? 'rgba(59,130,246,0.06)' : 'rgba(220,38,38,0.06)';
    }

    /**
     * Drawing of real price + consensus. `drawP` is 0→1 (global across
     * PHASE1_END → DRAW_END). Two-stage schedule so that by the TRIGGER
     * moment exactly 70% of the points are drawn (last one touching the
     * Optimista line) and the remaining 30% finish afterwards.
     */
    function updateDrawing(drawP) {
      drawP = Math.max(Math.min(drawP, 1), 0);
      chart.data.datasets[6].data = evoPoints.slice(0, Math.floor(evoPoints.length * drawP));
      chart.data.datasets[5].data = interpolatePoints(consensusLine, drawP);
    }

    function completeDrawing() {
      chart.data.datasets[6].data = evoPoints.slice();
      chart.data.datasets[5].data = consensusLine.slice();
    }

    function updateValidation(p) {
      /* p: 0→1 elapsed since TRIGGER.
         The hero always validates Optimista (bull) — its trajectory is the
         one the synthesized real-price line has been chasing. */
      var validatedIdx = 2;
      var fadeIdxs = [3, 4];

      /* Validated line: solid, thicker (smooth ease) */
      var eased = Math.min(p * 1.2, 1);
      chart.data.datasets[validatedIdx].borderDash = [];
      chart.data.datasets[validatedIdx].borderWidth = 1.6 + (1.6 * eased);
      chart.data.datasets[validatedIdx].borderColor = '#15803d';

      /* Fade base/bear */
      var fadeAlpha = 1 - (0.55 * eased);
      fadeIdxs.forEach(function(i) {
        /* Respect swapped colors: dataset 3 = Base, dataset 4 = Bear */
        var rgb;
        if (swapBaseBear) {
          rgb = i === 3 ? '220,38,38' : '59,130,246';
        } else {
          rgb = i === 3 ? '59,130,246' : '220,38,38';
        }
        chart.data.datasets[i].borderColor = 'rgba(' + rgb + ',' + fadeAlpha.toFixed(3) + ')';
      });

      /* Badge + tags appear immediately at trigger (p ≥ 0) */
      validationBadge.classList.add('is-visible');
      validationTags.classList.add('is-visible');
    }

    function positionDetectionMarker() {
      if (!detectionPoint || !chart || !chart.scales || !chart.scales.x) return;
      var canvasEl = chart.canvas;
      if (!canvasEl) return;
      var canvasRect = canvasEl.getBoundingClientRect();
      var wrapRect = container.getBoundingClientRect();
      var xPx = chart.scales.x.getPixelForValue(detectionPoint.x);
      var yPx = chart.scales.y.getPixelForValue(detectionPoint.y);
      if (!isFinite(xPx) || !isFinite(yPx)) return;
      var left = (canvasRect.left - wrapRect.left) + xPx;
      var top = (canvasRect.top - wrapRect.top) + yPx;
      detectionMarker.style.left = left + 'px';
      detectionMarker.style.top = top + 'px';
    }

    function applyBullCue(cueP) {
      /* Pre-highlight cue on Optimista line just before the trigger:
         progressively thicken and fully-saturate its color. */
      var eased = Math.max(Math.min(cueP, 1), 0);
      chart.data.datasets[2].borderWidth = 1.6 + 0.8 * eased;
      chart.data.datasets[2].borderColor = '#16a34a';
    }

    function updateStepHighlight(progress) {
      var activeStep;
      if (progress < PHASE1_END) { activeStep = 1; }
      else if (progress < TRIGGER) { activeStep = 2; }
      else { activeStep = 3; }

      stepEls.forEach(function(el) {
        var n = parseInt(el.getAttribute('data-step'), 10);
        el.classList.toggle('is-active', n === activeStep);
        el.classList.toggle('is-done', n < activeStep);
      });
    }

    function updateAlerts(progress) {
      alertEls.forEach(function(el, idx) {
        if (!el) return;
        var w = ALERT_WINDOWS[idx];
        var visible = progress >= w.start && progress <= w.end;
        el.classList.toggle('is-visible', visible);
      });
    }

    /* Throttle chart.update to ~30fps */
    var lastUpdate = 0;
    var UPDATE_MS = 33;

    function tick(now) {
      if (!startTime) startTime = now;
      var elapsed = now - startTime;
      var progress = Math.min(elapsed / opts.duration, 1);

      updateStepHighlight(progress);
      updateAlerts(progress);

      if (progress <= PHASE1_END) {
        /* Phase 1: scenarios only */
        updatePhase1(progress / PHASE1_END);
      } else {
        /* Scenarios already drawn, lock them in */
        completePhase1();

        /* Two-stage drawing schedule:
             PHASE1_END → TRIGGER : 0% → DRAW_AT_TRIGGER drawn
             TRIGGER    → DRAW_END: DRAW_AT_TRIGGER → 100% drawn
           By TRIGGER, the last two real-price points are sitting on the
           Optimista line (visible "very close" state). */
        var drawP;
        if (progress <= TRIGGER) {
          drawP = ((progress - PHASE1_END) / (TRIGGER - PHASE1_END)) * DRAW_AT_TRIGGER;
        } else if (progress <= DRAW_END) {
          drawP = DRAW_AT_TRIGGER + ((progress - TRIGGER) / (DRAW_END - TRIGGER)) * (1 - DRAW_AT_TRIGGER);
        } else {
          drawP = 1;
        }
        updateDrawing(drawP);

        /* Pre-highlight cue on Optimista in the run-up to the trigger */
        if (progress >= PHASE1_END && progress < TRIGGER) {
          var cueStart = PHASE1_END + (TRIGGER - PHASE1_END) * 0.55;
          if (progress >= cueStart) {
            applyBullCue((progress - cueStart) / (TRIGGER - cueStart));
          }
        }

        /* From TRIGGER onward: validation visuals (badge, tags, line
           highlight, fades) run in parallel with the remaining drawing */
        if (progress >= TRIGGER) {
          var vP = (progress - TRIGGER) / (1 - TRIGGER);
          updateValidation(vP);
          positionDetectionMarker();
          detectionMarker.classList.add('is-visible');
        }

        if (progress >= DRAW_END) {
          completeDrawing();
        }
      }

      if (now - lastUpdate >= UPDATE_MS || progress >= 1) {
        chart.update('none');
        lastUpdate = now;
      }

      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        /* Final: mark all steps done */
        stepEls.forEach(function(el) { el.classList.add('is-done'); el.classList.remove('is-active'); });
        container.dataset.animated = '1';
      }
    }

    function startAnimation() {
      if (rafId) cancelAnimationFrame(rafId);
      startTime = null;
      lastUpdate = 0;
      resetState();
      rafId = requestAnimationFrame(tick);
    }

    /* IntersectionObserver trigger */
    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && container.dataset.animated !== '1') {
          io.disconnect();
          startAnimation();
        }
      });
    }, { threshold: 0.3 });
    io.observe(container);

    /* Replay button */
    var replayBtn = container.querySelector('[data-hac-replay]');
    if (replayBtn) {
      replayBtn.addEventListener('click', function() {
        container.dataset.animated = '';
        startAnimation();
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-hero-chart]').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
